import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  onSnapshot,
  addDoc,
  updateDoc,
  serverTimestamp,
  deleteDoc,
} from "firebase/firestore";
import Nav from "./Nav";
import Swal from "sweetalert2";
import moment from "moment";
import Spinner from "./Spinner";
import Sidebar from "./Sidebar";
import { useNavContext } from "../context/NavContext";
import { orgCollection, orgDocIn } from "../utils/firePaths";
import { useAuth } from "../context/AuthContext";
import Breadcrumb from "./Breadcrumb";
import ContactDetailModal from "./ContactDetailModal";
import ContactFormModal from "./ContactFormModal";
import TableToolbar from "./TableToolbar";
import DataTable from "./DataTable";
import TablePagination from "./TablePagination";

moment.locale("es");

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
    maxWidth: "650px",
    borderRadius: "15px",
    padding: "0",
    border: "none",
    boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
    overflow: "hidden",
    fontFamily: "'Inter', sans-serif",
  },
  overlay: { backgroundColor: "rgba(0, 0, 0, 0.4)", zIndex: 1009 },
};

const viewModalStyles = {
  ...customStyles,
  content: {
    ...customStyles.content,
    maxWidth: "850px",
  },
};

const ListaProveedores = () => {
  const { user, orgId } = useAuth();
  const { proximasAVencer, proximosEventos } = useNavContext();
  const [proveedores, setProveedores] = useState([]);
  const [formData, setFormData] = useState({
    empresa: "",
    rfc: "",
    telefono: "",
    correo: "",
    domicilio: "",
    numeroRegistro: "",
    contactoPersonal: "",
  });
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [editingProveedorId, setEditingProveedorId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [viewModalIsOpen, setViewModalIsOpen] = useState(false);
  const [selectedProveedor, setSelectedProveedor] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: "numeroRegistro",
    direction: "asc",
  });

  const handleSort = useCallback((key) => {
    setSortConfig((prev) => {
      const direction =
        prev.key === key && prev.direction === "asc" ? "desc" : "asc";
      return { key, direction };
    });
  }, []);

  const parseFecha = (fecha) => {
    if (!fecha) return null;
    if (fecha.seconds) return new Date(fecha.seconds * 1000);
    if (fecha.toDate) return fecha.toDate();
    const d = new Date(fecha);
    return isNaN(d.getTime()) ? null : d;
  };

  const [visibleColumns, setVisibleColumns] = useState(() => {
    const guardado = localStorage.getItem("columnConfig_proveedores");
    const defaultConfig = {
      empresa: true,
      correo: true,
      telefono: true,
      fechaRegistro: true,
    };
    return guardado ? JSON.parse(guardado) : defaultConfig;
  });

  const handleEditSelected = () => {
    if (selectedIds.length === 1) {
      const proveedorParaEditar = proveedores.find(
        (i) => i.id === selectedIds[0],
      );
      if (proveedorParaEditar) {
        setFormData(proveedorParaEditar);
        setEditingProveedorId(proveedorParaEditar.id);
        setModalIsOpen(true);
      }
    }
  };

  const handleDeleteSelected = async () => {
    if (!user?.uid || !orgId) {
      Swal.fire({ title: "Error", text: "Sesión no válida.", icon: "error" });
      return;
    }

    if (selectedIds.length === 0) return;

    const result = await Swal.fire({
      html: `
      <div class="custom-modal-container">
        <div class="custom-modal-header">
          <div class="custom-modal-icon-box">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" x2="12" y1="8" y2="12"></line><line x1="12" x2="12.01" y1="16" y2="16"></line></svg>
          </div>
          <h3 class="custom-modal-title">Eliminar Proveedores</h3>
        </div>
       
        <p class="custom-modal-description">¿Estás seguro de que deseas eliminar ${selectedIds.length} proveedor(es)?</p>
       
        <div class="custom-modal-actions">
          <button id="cancel-btn" class="btn-modal-cancel">Cancelar</button>
          <button id="confirm-btn" class="btn-modal-delete">Eliminar</button>
        </div>
      </div>
    `,
      showConfirmButton: false,
      showCancelButton: false,
      buttonsStyling: false,
      allowOutsideClick: false,
      customClass: {
        popup: "swal-popup-reset",
        htmlContainer: "swal-html-reset",
      },
      didOpen: () => {
        document
          .getElementById("cancel-btn")
          .addEventListener("click", () => Swal.close());
        document
          .getElementById("confirm-btn")
          .addEventListener("click", () => Swal.clickConfirm());
      },
    });

    if (!result.isConfirmed) return;

    try {
      await Promise.all(
        selectedIds.map(async (id) => {
          await deleteDoc(orgDocIn(orgId, "proveedores", id));
        }),
      );
      setSelectedIds([]);
      if (typeof setViewModalIsOpen === "function") setViewModalIsOpen(false);

      Swal.fire({
        icon: "success",
        title: "Completado",
        text: "Eliminado correctamente",
        timer: 1500,
        showConfirmButton: false,
        customClass: {
          popup: "swal-popup-reset",
        },
      });
    } catch (error) {
      Swal.fire({ icon: "error", title: "Error", text: error.message });
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      const clickedInColumnMenu = e.target.closest(
        ".dropdown-custom.column-config",
      );
      const clickedInColumnBtn = e.target.closest('[data-dd="columns"]');

      const clickedInExportMenu = e.target.closest(
        ".dropdown-custom.export-menu",
      );
      const clickedInExportBtn = e.target.closest('[data-dd="export"]');

      if (showColumnConfig && !clickedInColumnMenu && !clickedInColumnBtn) {
        setShowColumnConfig(false);
      }

      if (showExportDropdown && !clickedInExportMenu && !clickedInExportBtn) {
        setShowExportDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside, true);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside, true);
  }, [showColumnConfig, showExportDropdown]);

  useEffect(() => {
    if (!user?.uid || !orgId) return;

    setIsLoading(true);

    const unsub = onSnapshot(orgCollection(orgId, "proveedores"), (snap) => {
      setProveedores(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setIsLoading(false);
    });

    return () => unsub();
  }, [user?.uid, orgId]);

  const resetFormData = useCallback(() => {
    setFormData({
      empresa: "",
      rfc: "",
      telefono: "",
      correo: "",
      domicilio: "",
      numeroRegistro: "",
      contactoPersonal: "",
    });
    setEditingProveedorId(null);
  }, []);

  const handleOpenModal = () => {
    resetFormData();
    setModalIsOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user?.uid || !orgId) {
      return Swal.fire("Error", "Sesión no válida.", "error");
    }

    if (!formData.empresa.trim()) {
      return Swal.fire("Error", "La Razón Social es obligatoria", "error");
    }

    const colRef = orgCollection(orgId, "proveedores");

    // ⚠️ Importante: no fuerces fechaRegistro en edición para no sobrescribirla
    const datosAGuardar = {
      ...formData,
      tipo: "Proveedor",
    };

    // solo en creación
    if (!editingProveedorId) {
      datosAGuardar.fechaRegistro = serverTimestamp();
    }

    // limpiar undefined
    Object.keys(datosAGuardar).forEach(
      (k) => datosAGuardar[k] === undefined && delete datosAGuardar[k],
    );

    try {
      if (editingProveedorId) {
        await updateDoc(orgDocIn(orgId, "proveedores", editingProveedorId), {
          ...datosAGuardar,
          updatedAt: serverTimestamp(),
        });
      } else {
        await addDoc(colRef, {
          ...datosAGuardar,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }

      setSelectedIds([]);
      setModalIsOpen(false);
      setEditingProveedorId(null);

      Swal.fire({
        icon: "success",
        title: "Guardado",
        timer: 1000,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Error al guardar:", err);
      Swal.fire("Error", "No se pudo guardar la información", "error");
    }
  };

  const handleAutoAsignar = () => {
    let maxNum = 0;
    proveedores.forEach((p) => {
      if (p.numeroRegistro?.startsWith("P-")) {
        const parts = p.numeroRegistro.split("-");
        const n = parseInt(parts[1]);
        if (!isNaN(n) && n > maxNum) maxNum = n;
      }
    });
    setFormData((prev) => ({
      ...prev,
      numeroRegistro: `P-${String(maxNum + 1).padStart(4, "0")}`,
    }));
  };

  const filteredData = useMemo(() => {
    const s = (searchQuery || "").toLowerCase();
    return proveedores.filter((p) => {
      const empresa = (p.empresa || "").toLowerCase();
      const rfc = (p.rfc || "").toLowerCase();
      const correo = (p.correo || "").toLowerCase();
      const telefono = (p.telefono || "").toString().toLowerCase();
      const numeroRegistro = (p.numeroRegistro || "").toLowerCase();

      return (
        empresa.includes(s) ||
        rfc.includes(s) ||
        correo.includes(s) ||
        telefono.includes(s) ||
        numeroRegistro.includes(s)
      );
    });
  }, [proveedores, searchQuery]);

  const sortedData = useMemo(() => {
    const { key, direction } = sortConfig;

    const getNumRegistro = (str) => {
      if (!str) return 0;
      const parts = String(str).split("-");
      return parts.length > 1 ? parseInt(parts[1], 10) : 0;
    };

    return [...filteredData].sort((a, b) => {
      // fechaRegistro
      if (key === "fechaRegistro") {
        const fa = parseFecha(a.fechaRegistro);
        const fb = parseFecha(b.fechaRegistro);
        const A = fa ? fa.getTime() : 0;
        const B = fb ? fb.getTime() : 0;
        return direction === "asc" ? A - B : B - A;
      }

      // numeroRegistro (P-0001)
      if (key === "numeroRegistro") {
        const A = getNumRegistro(a.numeroRegistro);
        const B = getNumRegistro(b.numeroRegistro);
        return direction === "asc" ? A - B : B - A;
      }

      // texto
      const A = (a[key] ?? "").toString().toLowerCase();
      const B = (b[key] ?? "").toString().toLowerCase();
      return direction === "asc" ? A.localeCompare(B) : B.localeCompare(A);
    });
  }, [filteredData, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / itemsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  const currentItems = useMemo(() => {
    return sortedData.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage,
    );
  }, [sortedData, currentPage, itemsPerPage]);

  const toggleRow = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  const toggleSelectAll = useCallback(() => {
    const ids = currentItems.map((r) => r.id);
    const allSelected =
      ids.length > 0 && ids.every((id) => selectedIds.includes(id));
    setSelectedIds((prev) =>
      allSelected
        ? prev.filter((x) => !ids.includes(x))
        : [...new Set([...prev, ...ids])],
    );
  }, [currentItems, selectedIds]);

  const columnOptionsProveedores = useMemo(
    () => [
      { key: "empresa", label: "Razón Social" },
      { key: "correo", label: "Correo" },
      { key: "telefono", label: "Teléfono" },
      { key: "fechaRegistro", label: "Fecha de Registro" },
    ],
    [],
  );

  const columnsProveedores = useMemo(() => {
    return [
      {
        id: "empresa",
        label: "Razón Social",
        visible: visibleColumns.empresa,
        sortKey: "empresa",
        cellClassName: "table-td col-left",
        render: (p) => p.empresa || "---",
      },
      {
        id: "correo",
        label: "Correo",
        visible: visibleColumns.correo,
        sortKey: "correo",
        cellClassName: "table-td col-center",
        render: (p) => p.correo || "---",
      },
      {
        id: "telefono",
        label: "Teléfono",
        visible: visibleColumns.telefono,
        sortKey: "telefono",
        cellClassName: "table-td col-center",
        render: (p) => p.telefono || "---",
      },
      {
        id: "fechaRegistro",
        label: "Fecha de Registro",
        visible: visibleColumns.fechaRegistro,
        sortKey: "fechaRegistro",
        cellClassName: "table-td col-center",
        render: (p) => {
          const f = parseFecha(p.fechaRegistro);
          return f ? moment(f).format("DD/MM/YYYY") : "N/A";
        },
      },
    ];
  }, [visibleColumns]);

  return (
    <>
      <div className="no-sticky">
        <Nav
          proximasAVencer={proximasAVencer}
          proximosEventos={proximosEventos}
        />
        <Sidebar />

        <div className="main-content-wrapper">
          <div style={{ marginBottom: "25px" }}>
            <Breadcrumb />
            <h1 className="page-title">Proveedores</h1>
          </div>

          <TableToolbar
            // búsqueda
            searchTerm={searchQuery}
            setSearchTerm={setSearchQuery}
            searchPlaceholder="Buscar proveedores..."
            // columnas
            showColumnConfig={showColumnConfig}
            setShowColumnConfig={setShowColumnConfig}
            visibleColumns={visibleColumns}
            setVisibleColumns={setVisibleColumns}
            columnOptions={columnOptionsProveedores}
            // export
            showExportDropdown={showExportDropdown}
            setShowExportDropdown={setShowExportDropdown}
            fileInputRef={null}
            onExport={() => setShowExportDropdown(false)}
            // nuevo
            onAdd={handleOpenModal}
            addButtonTooltip="Nuevo"
          />

          {/* BARRA DE SELECCIÓN */}
          {selectedIds.length > 0 && (
            <div className="selection-bar">
              <span>
                {selectedIds.length === 1
                  ? "1 seleccionado"
                  : `${selectedIds.length} seleccionado(s)`}
              </span>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  className="btn-selection btn-edit"
                  style={{ opacity: selectedIds.length > 1 ? 0.5 : 1 }}
                  onClick={handleEditSelected}
                  disabled={selectedIds.length > 1}
                >
                  <i className="bi bi-pencil-square" /> <span>Editar</span>
                </button>
                <button
                  className="btn-selection btn-delete"
                  onClick={handleDeleteSelected}
                >
                  <i className="bi bi-trash" /> <span>Eliminar</span>
                </button>
              </div>
            </div>
          )}

          <DataTable
            loading={isLoading}
            loadingFallback={
              <div style={{ padding: "50px" }}>
                <Spinner />
              </div>
            }
            rows={currentItems}
            columns={columnsProveedores}
            selectedIds={selectedIds}
            onToggleAll={toggleSelectAll}
            onToggleRow={toggleRow}
            sortConfig={sortConfig}
            onSort={handleSort}
            onRowClick={(row) => {
              setSelectedProveedor(row);
              setViewModalIsOpen(true);
            }}
            emptyTitle="Aún no hay proveedores."
            emptySubtitle="Crea un proveedor para comenzar."
            emptyIcon={
              <img
                src="/img/no-data.svg"
                alt="Sin datos"
                style={{ width: 220, maxWidth: "70%", opacity: 0.95 }}
              />
            }
            footer={
              <TablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(p) => setCurrentPage(p)}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={(n) => {
                  setItemsPerPage(n);
                  setCurrentPage(1);
                }}
                itemsPerPageOptions={[5, 10, 20]}
                selectedCount={selectedIds.length}
                selectedLabelSingular="proveedor"
                selectedLabelPlural="proveedores"
                itemsLabel="Mostrando:"
              />
            }
          />
        </div>
      </div>

      <ContactFormModal
        isOpen={modalIsOpen}
        onClose={() => setModalIsOpen(false)}
        formData={formData}
        setFormData={setFormData}
        handleSubmit={handleSubmit}
        handleAutoAsignar={handleAutoAsignar}
        isEditing={!!editingProveedorId}
        hideTypeSelector={true}
        defaultType="Proveedor"
      />
      <ContactDetailModal
        isOpen={viewModalIsOpen}
        onClose={() => setViewModalIsOpen(false)}
        contact={
          selectedProveedor ? { ...selectedProveedor, tipo: "Proveedor" } : null
        }
        modalStyles={viewModalStyles}
      />
    </>
  );
};

export default ListaProveedores;
